/* ---------------------------------------------------------------------------
   Cómo se leen las cifras del catálogo de mercado.

   Vive aparte de `components/informes/formato.ts` porque aquel es el GEMELO de
   `App\Support\FormatoDelInforme` —cambiar uno obliga a cambiar el otro en el
   mismo commit, porque los dos imprimen el documento del cliente— y esto no
   tiene gemelo: son cifras de pantalla que ningún papel imprime.

   UN CAMPO VACÍO NO ES UN CERO. Es la regla que atraviesa todo el archivo: un
   aviso sin precio publicado no vale cero pesos, y una superficie construida
   sin medir no significa que no haya construcción. Todo lo que falta sale como
   "—" y nunca como un número.
--------------------------------------------------------------------------- */

const ES = "es-CL";

/**
 * Un número con separador de miles, o "—".
 *
 * `null` y `undefined` salen con guion; el CERO no, porque un cero medido es un
 * dato y no un vacío.
 */
export function numero(valor: number | null | undefined, decimales = 0): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return "—";

  return valor.toLocaleString(ES, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

/** Metros cuadrados leídos en hectáreas, que es la unidad de un predio rural. */
export function hectareas(m2: number | null | undefined): string {
  if (m2 === null || m2 === undefined) return "—";

  return numero(m2 / 10_000, 2);
}

/**
 * Pesos chilenos. Sin decimales: un predio no se cotiza al peso, y los
 * decimales solo agregan ruido a una cifra de nueve dígitos.
 */
export function pesos(monto: number | null | undefined): string {
  if (monto === null || monto === undefined) return "—";

  return `$ ${numero(Math.round(monto))}`;
}

export function uf(valor: number | null | undefined, decimales = 0): string {
  if (valor === null || valor === undefined) return "—";

  return `UF ${numero(valor, decimales)}`;
}

/**
 * Una distancia en metros, leída como la diría una persona.
 *
 * Bajo el kilómetro se dan los metros redondos: "740 m" ubica y "0,74 km" hay
 * que traducirlo mentalmente.
 */
export function distancia(metros: number | null | undefined): string {
  if (metros === null || metros === undefined) return "—";
  if (metros < 1000) return `${numero(Math.round(metros))} m`;

  return `${numero(metros / 1000, 2)} km`;
}

/**
 * Una fecha ISO como día/mes/año.
 *
 * Se parte el string en vez de construir un `Date`: `new Date("2026-03-14")` se
 * interpreta como MEDIANOCHE UTC, y al leerlo en hora de Chile devuelve el día
 * anterior. Estas fechas son de calendario —una inscripción, una publicación—,
 * no instantes, así que no tienen zona que convertir.
 */
export function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return "—";

  const [anio, mes, dia] = iso.slice(0, 10).split("-");
  if (!anio || !mes || !dia) return "—";

  return `${dia}/${mes}/${anio}`;
}

/**
 * Cuánto se aparta un valor de la mediana, en por ciento.
 *
 * `null` cuando falta cualquiera de los dos o cuando la mediana es cero: un
 * desvío contra cero no es infinito, es una cuenta que no se puede hacer.
 */
export function desvioContraLaMediana(
  valor: number | null | undefined,
  mediana: number | null | undefined,
): number | null {
  if (valor === null || valor === undefined) return null;
  if (mediana === null || mediana === undefined || mediana === 0) return null;

  return ((valor - mediana) / mediana) * 100;
}

/**
 * Cómo se rotula ese desvío.
 *
 * Bajo el 8% se lee "en el rango" y no un porcentaje: el diseño lo trata como
 * ruido, y un "+2%" invita a explicar una diferencia que no significa nada
 * frente a la dispersión normal de un mercado de suelo.
 */
export const DESVIO_QUE_ES_RUIDO = 8;

export function describirDesvio(desvio: number | null): string {
  if (desvio === null) return "Sin valor unitario";
  if (Math.abs(desvio) < DESVIO_QUE_ES_RUIDO) return "En el rango de la mediana";

  const signo = desvio > 0 ? "+" : "−";

  return `${signo}${numero(Math.abs(desvio), 0)}% vs. mediana`;
}

/**
 * La media de una lista, ignorando lo que no se pudo calcular.
 *
 * ES LA MEDIA Y NO LA MEDIANA, y la distinción no es de estilo: es la cifra que
 * el tasador toma como valor de referencia del conjunto que eligió a mano, y el
 * método lo fija quien tasa. Hasta el 08-09-2026 acá se calculaba la mediana
 * bajo el mismo rótulo.
 *
 * LO QUE HAY QUE SABER AL LEERLA: la media se corre con un solo comparable
 * extremo, y este catálogo los tiene —la mediana de UF/ha va de 288 en predios
 * de más de 50 ha a 85.422 bajo 0,1 ha—. Sobre un puñado de fichas elegidas a
 * mano eso es manejable; es la misma razón por la que el gráfico compara contra
 * la MEDIANA del filtro, que son miles de fichas y ninguna elegida.
 *
 * Un valor que no se pudo calcular NO promedia como cero. `uf_ha_terreno` es
 * null cuando no hay con qué calcularlo —un aviso sin precio— y contarlo como
 * cero hundiría la cifra con un dato que no existe. Es la misma regla por la
 * que el catálogo filtra con `valor_plausible IS NOT FALSE` y nunca con
 * `= true`.
 */
export function media(valores: (number | null | undefined)[]): number | null {
  const limpios = valores.filter((v): v is number => v !== null && v !== undefined && v > 0);

  if (limpios.length === 0) return null;

  return limpios.reduce((suma, v) => suma + v, 0) / limpios.length;
}

/**
 * El valor unitario en pesos por hectárea.
 *
 * ES UNA CUENTA Y NO UN CAMPO DEL CATÁLOGO, a diferencia de su gemela en UF
 * (`uf_ha_terreno`), que llega calculada del servidor. Vive acá y no escrita a
 * mano en cada pantalla porque la tarjeta y el tooltip del mapa muestran la
 * misma cifra del MISMO comparable: con dos cuentas separadas, el día que
 * discrepen darían dos precios para una sola ficha.
 *
 * SE NIEGA SI EL PRECIO NO ESTÁ EN PESOS. El catálogo trae montos en otras
 * monedas, y rotular un monto en dólares como "$/ha" no es un redondeo: es una
 * cifra falsa en la pantalla desde la que se arma una tasación. Un `null` en la
 * moneda tampoco se lee como "serán pesos".
 *
 * Y NUNCA DIVIDE POR CERO: una superficie en cero daría `Infinity`, que el
 * formateador imprimiría como si fuera un precio.
 */
export function valorUnitarioEnPesos(ficha: {
  precio_monto: number | null;
  precio_moneda: string | null;
  superficie_terreno_m2: number | null;
}): number | null {
  if (ficha.precio_moneda !== "CLP") return null;
  if (ficha.precio_monto === null) return null;
  if (!ficha.superficie_terreno_m2) return null;

  return ficha.precio_monto / (ficha.superficie_terreno_m2 / 10_000);
}

/**
 * Cómo se nombra en pantalla la precisión de una coordenada.
 *
 * El vocabulario crudo del catálogo (`centroide_comuna`) no se muestra nunca:
 * es de la base de ingesta y no significa nada para un tasador.
 */
export function describirPrecision(precision: string | null): string {
  switch (precision) {
    case "exacta":
      return "Coordenada exacta";
    case "aproximada":
      return "Ubicación aproximada";
    case "centroide_comuna":
      return "Centro de la comuna";
    default:
      return "Ubicación desconocida";
  }
}
